import { useRef, useState } from 'react'
import { Upload, X, Loader2 } from 'lucide-react'
import { uploadAsset, getAssetUrl } from '@/lib/api'

interface Props {
  value: string | null
  onChange: (url: string | null) => void
  label?: string
}

export function ImageUpload({ value, onChange, label = 'Image' }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setUploading(true)
    setError(null)
    try {
      const { url } = await uploadAsset(file)
      onChange(url)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const displayUrl = value ? getAssetUrl(value) : null

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>

      {displayUrl ? (
        <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
          <img
            src={displayUrl}
            alt="Preview"
            className="w-full h-36 object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
            title="Remove image"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 right-2 bg-white/90 text-slate-700 text-xs rounded-lg px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white border border-slate-200"
          >
            Replace
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            uploading
              ? 'border-brand-300 bg-brand-50/30 cursor-default'
              : 'border-slate-200 hover:border-brand-300 hover:bg-slate-50 cursor-pointer'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-brand-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-xs font-medium">Uploading…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <Upload className="h-6 w-6" />
              <span className="text-xs">Click to upload or drag & drop</span>
              <span className="text-[10px] text-slate-300">JPEG, PNG, WebP · max 8 MB</span>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  )
}
